<template>
  <b-container>
    <b-row>
      <b-col>
        <div class="form form-sm my-4 my-lg-5 mx-auto">
          <form-header v-if="!showSetPassword" bookmarkable>
            <template #sub-heading>
              <span class="d-block mb-0 small"
                >Enter your email address to sign in.</span
              >
            </template>
          </form-header>

          <SsoSetPassword
            v-if="showSetPassword"
            :email="formData.email"
            :sso-login-url="ssoLoginUrl"
          />

          <b-alert
            v-if="showFirstTimePrompt"
            variant="primary"
            show
            class="text-center mb-4"
          >
            <p class="mb-3">
              Due to the planned implementation of SSO capabilities and enhanced
              security protocols, all users are required to change their
              password. Please select <strong>NEXT</strong> to reset your
              password and login.
            </p>
            <base-button type="primary" size="lg" block @click="continueToSso">
              Next
            </base-button>
          </b-alert>

          <validation-observer
            v-if="!showFirstTimePrompt && !showSetPassword"
            v-slot="{ invalid }"
            ref="formRef"
            class="form form-sm mx-auto"
          >
            <b-form @submit.prevent="handleSubmit">
              <base-input
                v-model="formData.email"
                rules="required|email"
                name="Email Address"
                label="Email Address"
                @focus="() => (successMessage = '')"
              >
                <template v-if="!!successMessage" #success>
                  <b-alert
                    variant="primary"
                    class="py-1 px-3 mt-2 text-center"
                    show
                    >{{ successMessage }}</b-alert
                  >
                </template>
              </base-input>

              <TermlyCheckbox v-model="formData.terms" />

              <base-button
                :disabled="invalid || checkingSso"
                :loading="formProcessing"
                type="primary"
                native-type="submit"
                size="lg"
                class="mt-4"
                block
                >{{ checkingSso ? "Checking sign-in method..." : "Log In" }}</base-button
              >
            </b-form>
          </validation-observer>

          <!-- The hand-off to the two-factor login page was removed: migrated
               users now reach SSO from the button above, so it was a second
               route to the same place. The expiry warning only applies once a
               link has actually been sent (successMessage is set on that
               response) - showing it unconditionally on page load claimed a
               link would "expire in 10 minutes" before anyone had typed
               anything, which read as a magic-link page even for the
               majority of users who are redirected to SSO and never receive
               a link at all. -->
          <div
            v-if="!showFirstTimePrompt && !showSetPassword && successMessage"
            class="mt-4 font-weight-bold text-center small"
          >
            Please do not forward your link to any other party. It will expire
            in 10 minutes.
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Vue from 'vue'
import { defineComponent, reactive, ref, onMounted } from '@vue/composition-api'
import { useRoute } from 'vue2-helpers/vue-router'

import { AuthenticationService } from '@services'
import { useSsoLogin } from '@composables'

import FormHeader from '@components/FormHeader'
import SsoSetPassword from '@components/SsoSetPassword'
import TermlyCheckbox from '@components/Termly/TermlyCheckbox'

export default defineComponent({
  title: 'Login',
  components: {
    FormHeader,
    SsoSetPassword,
    TermlyCheckbox
  },
  setup() {
    // Pre-fill from ?email=, which arrives two ways: straight from an SSO invite
    // link, or carried over when /login redirects here because passwords are off
    // for this environment.
    const route = useRoute()
    const prefillEmail = (route.query.email || '').trim()

    // Initialise email at creation time so Vue 2 reactivity tracks it.
    const formData = reactive({ email: prefillEmail })

    const formRef = ref()
    const formProcessing = ref(false)
    const successMessage = ref('')

    // Sign-in routing is shared with the password login page - see useSsoLogin.
    const {
      checkingSso,
      showFirstTimePrompt,
      showSetPassword,
      ssoLoginUrl,
      loadAuthConfig,
      resolveSsoForEmail,
      continueToSso
    } = useSsoLogin()

    // Show a message if the SSO round-trip bounced back with a failure.
    // The backend redirects failed logins to this page with
    // ?sso_error=1 and creates no session; here we surface a friendly message.
    onMounted(() => {
      loadAuthConfig()

      // An invited user arrives with their address already known, so resolve it
      // now and show them the first-time prompt (or the set-password screen)
      // rather than a form they would only retype their own email into.
      if (prefillEmail) resolveSsoForEmail(prefillEmail)

      const params = new URLSearchParams(window.location.search)
      if (params.get('sso_error')) {
        // "or sign in another way" previously implied a ready alternative -
        // misleading now that SSO is the only path for virtually everyone;
        // the one remaining non-migrated account still has magic link via
        // the form below regardless of this message's wording.
        Vue.prototype.$toasted.show(
          'Single sign-on was unsuccessful. Please try again.',
          { type: 'error' }
        )
      }
    })

    const handleSubmit = async () => {
      // Only now - after the user has entered a valid email, accepted the
      // terms, and clicked Log In (the button stays disabled until all three) -
      // do we resolve the sign-in method. Migrated accounts are handed off to
      // Entra SSO; everyone else falls back to the emailed magic link.
      if (await resolveSsoForEmail(formData.email)) return

      formProcessing.value = true
      successMessage.value = ''

      let ssoRedirected = false
      const res = await AuthenticationService.magicLogin(formData.email).catch(
        (err) => {
          formProcessing.value = false
          // The magic-link endpoint is gated by the same environment flag as
          // password sign-in, so it answers 403 + sso_login_url when legacy
          // login is off. Discarding that left the button doing nothing at all
          // and no message on screen; send them where the server is pointing,
          // and say something for every other failure.
          const body = (err && err.response && err.response.data) || {}
          const payload = body.data || body
          if (payload.sso_required && payload.sso_login_url) {
            ssoRedirected = true
            window.location.href = payload.sso_login_url
            return undefined
          }
          Vue.prototype.$toasted.show(
            payload.detail ||
              "We couldn't send a login link. Please try again.",
            { type: 'error' }
          )
          return undefined
        }
      )

      if (ssoRedirected || 'undefined' == typeof res) return

      const { data } = res.data

      formRef.value.reset()

      formData.email = ''
      formProcessing.value = false

      successMessage.value = data.message
    }

    return {
      formData,
      formRef,
      formProcessing,
      successMessage,
      checkingSso,
      showFirstTimePrompt,
      showSetPassword,
      // Bound by the SsoSetPassword above; without it that component received
      // undefined and lost its fallback link to the hosted Entra page.
      ssoLoginUrl,
      continueToSso,
      handleSubmit
    }
  }
})
</script>
